<template>
  <div class="min-h-screen bg-bg-primary text-text-primary font-sans transition-colors duration-300">
    <!-- Admin Header -->
    <AppHeader 
      v-if="shouldShowHeader" 
      :isSidebarOpen="sidebarOpen"
      :isAdminPanel="isAdminPanel || isStaffPanel"
      :isSuperAdminPanel="isSuperAdminPanel"
      @toggle-sidebar="toggleSidebar"
    >
      <template #actions>
        <ThemeToggle />
      </template>
    </AppHeader>

    <!-- Customer Header (nur wenn kein Admin/Staff/SuperAdmin-Header angezeigt wird) -->
    <CustomerHeader v-else /> 

    <!-- SideNav für Admin & Staff Bereich -->
    <SideNav 
      v-if="(isAdminPanel || isStaffPanel) && !isFullscreenLayout"
      :isOpen="sidebarOpen"
      :navItems="adminNavItems"
      title="Admin Panel"
      @close="sidebarOpen = false"
    />
    
    <!-- SideNav für SuperAdmin-Bereich -->
    <SideNav 
      v-if="isSuperAdminPanel && !isFullscreenLayout" 
      :isOpen="sidebarOpen"
      :navItems="superAdminNavItems"
      title="Super Admin"
      @close="sidebarOpen = false"
    />
    
    <!-- Overlay zum Schließen der Sidebar auf Mobilgeräten -->
    <SideNavOverlay 
      v-if="sidebarOpen && (isAdminPanel || isStaffPanel || isSuperAdminPanel) && !isFullscreenLayout"
      @close="sidebarOpen = false"
    />

    <!-- Padding: Header oben, Sidebar links nur auf >= lg -->
    <main :class="{
      'pt-16 md:pt-[68px]': !isFullscreenLayout,
      'lg:pl-[268px]': !isFullscreenLayout && sidebarOpen && (isAdminPanel || isStaffPanel || isSuperAdminPanel),
      'transition-all duration-300 ease-in-out': true
    }">
      <router-view v-slot="{ Component, route: rv }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="rv.fullPath" />
        </transition>
      </router-view>
    </main>

    <!-- ─── Gamification Global Overlays ──────────────────── -->
    <template v-if="isCustomerView && customerGamesOn">
      <GameHub
        @add-to-cart="handleGameAddToCart"
        @apply-discount="handleGameDiscount"
      />
      <BattleRoyale
        :order-session-id="customerSessionId"
        :session-identifier="customerSessionIdentifier"
      />
      <PrisonerDilemma
        @explore-category="navigateToCategory"
        @dismiss="() => {}"
      />
    </template>
    <AiChatbot
      v-if="isCustomerView"
      :order-session-id="customerSessionId"
      :guest-profile-id="guestProfileId"
      :enabled="gamificationStore.features.ai_chatbot"
    />

    <!-- Globales Audio Element für Benachrichtigungen -->
    <audio ref="globalNotificationSound" preload="auto"></audio>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SideNav from '@/components/layout/SideNav.vue'
import SideNavOverlay from '@/components/layout/SideNavOverlay.vue'
import AppHeader from '@/components/layout/AppHeader.vue'
import CustomerHeader from '@/components/layout/CustomerHeader.vue'
import ThemeToggle from '@/components/common/ThemeToggle.vue'
import { adminNavItems, superAdminNavItems } from '@/navigation/sideNavItems'
// Stores importieren
import { useAuthStore } from '@/stores/auth';
import { useOrderStore } from '@/stores/orders';
import { useRestaurantStore } from '@/stores/restaurant';
import { useGamificationStore } from '@/stores/gamification';
// Composables importieren
import { useNotifications } from '@/composables/useNotifications';
// Gamification Overlays
import BattleRoyale from '@/components/gamification/BattleRoyale.vue';
import PrisonerDilemma from '@/components/gamification/PrisonerDilemma.vue';
import AiChatbot from '@/components/gamification/AiChatbot.vue';
import GameHub from '@/components/gamification/GameHub.vue';
import { areCustomerGamesEnabled } from '@/utils/customerGames';

const route = useRoute()
const router = useRouter()
// Stores initialisieren
const authStore = useAuthStore();
const orderStore = useOrderStore();
const restaurantStore = useRestaurantStore();
const gamificationStore = useGamificationStore();
// Notifications-Composable initialisieren
const { 
  initializeSound, 
  playNotificationSound, 
  playOrderNotificationSound,
  showDesktopNotification, 
  requestNotificationPermission 
} = useNotifications();

// Ref für das globale Audio-Element
const globalNotificationSound = ref(null);

// UI state
// Standard: Desktop (>=1024) offen, Mobile geschlossen. localStorage darf das übersteuern.
const sidebarOpen = ref((() => {
  const stored = localStorage.getItem('sidebarOpen')
  if (stored === 'true') return true
  if (stored === 'false') return false
  return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
})())

// Parameter für QR-Code-Integration
const tableId = ref(null)
const sessionId = ref(null)

// Konzern-Admins / SuperAdmins arbeiten cross-property und sollen KEINE
// operativen Live-Bestellungen oder Küchen-Sounds bekommen.
const isOperationalRole = computed(() => {
  const r = (authStore.user?.role || '').toLowerCase()
  return !['hotel_group_admin', 'superadmin'].includes(r)
})

// --- Globale WebSocket Initialisierung --- 
watch(() => authStore.isAuthenticated, async (isAuth) => {
  if (isAuth && isOperationalRole.value) {
    // Wenn Benutzer authentifiziert ist, starte die Küchen-WS-Verbindung sofort
    console.log("App: Benutzer authentifiziert, initialisiere Küchen-System SOFORT...");
    
    // Starte beide Prozesse parallel für maximale Geschwindigkeit
    const promises = [
      // 1. Küchen-WebSocket/Polling initialisieren
      orderStore.initializeKitchenConnection().catch(error => {
        console.warn("App: Küchen-WebSocket Initialisierung Warnung:", error);
        // Kein throw - lass das System laufen
      }),
      
      // 2. Bestellungen laden
      orderStore.fetchOrders().catch(error => {
        console.warn("App: Bestellungen laden Warnung:", error);
      }),
      
      // 3. Kitchen-spezifische Daten laden
      orderStore.fetchKitchenOrders().catch(error => {
        console.warn("App: Kitchen-Bestellungen laden Warnung:", error);
      })
    ];
    
    // Warte auf alle Operationen, aber blockiere nicht bei Fehlern
    try {
      await Promise.allSettled(promises);
      console.log("App: Küchen-System Initialisierung abgeschlossen");
    } catch (error) {
      console.warn("App: Einige Initialisierungen fehlgeschlagen, System läuft trotzdem:", error);
    }
    
    // Zusätzlicher Fallback: Nach 2 Sekunden nochmal prüfen
    setTimeout(async () => {
      if (!isOperationalRole.value) return;
      const status = orderStore.getKitchenConnectionStatus;
      console.log("App: Kitchen-Status nach 2s:", status);
      if (status === 'disconnected') {
        console.log("App: Kitchen-Status ist disconnected, starte erneut...");
        orderStore.initializeKitchenConnection().catch(err => {
          console.warn("App: Fallback Kitchen-Init Warnung:", err);
        });
      }
    }, 2000);
    
  } else {
    // Wenn Benutzer sich ausloggt, Token ungültig wird ODER Konzern-Admin/Superadmin eingeloggt ist:
    // operative Live-Verbindung trennen bzw. erst gar nicht aufbauen.
    console.log("App: Keine operative Rolle oder nicht authentifiziert, trenne Küchen-System...");
    orderStore.disconnectKitchenConnection();
  }
}, { immediate: true }); // immediate: true, um es auch beim initialen Laden zu prüfen

// --- Watcher für Sound-Benachrichtigung --- 
watch(() => orderStore.newOrderSoundTrigger, (newValue, oldValue) => {
    if (!isOperationalRole.value) return; // Konzern-Admin / Superadmin: kein Bestell-Sound
    // Wir spielen den speziellen Ensu-Bip Sound ab, wenn sich der Trigger ändert (und nicht beim initialen Laden ist)
    if (newValue !== oldValue && oldValue !== undefined) { // Check gegen undefined verhindert Play beim ersten Laden
        playOrderNotificationSound();
    }
});

// --- Watcher für neue Zahlungsanfragen ---
watch(() => orderStore.waiterNotifications.hasNewPaymentRequest, (hasNewRequest) => {
  if (!isOperationalRole.value) return; // Konzern-Ebene bekommt keine Tisch-Zahlanfragen
  if (hasNewRequest) {
    // Wenn wir nicht auf dem Kellner-Dashboard sind, zeigen wir eine Desktop-Benachrichtigung
    if (route.path !== '/waiter-dashboard' && route.path !== '/admin/waiter-dashboard') {
      const count = orderStore.waiterNotifications.paymentRequests;
      if (count > 0) {
        const title = `${count} neue Zahlungsanfrage${count > 1 ? 'n' : ''}`;
        const message = 'Ein Tisch möchte bezahlen. Gehe zum Kellner-Dashboard, um Details zu sehen.';
        showDesktopNotification(title, message);
        playOrderNotificationSound(); // Verwende auch hier den Ensu-Sound
      }
    }
  }
}, { immediate: false });

// Automatische Weiterleitung beim App-Start, wenn bereits eingeloggt
onMounted(async () => {
  try {
    console.log('App wird initialisiert...');
    
    // Initialisiere den Restaurant-Store zuerst
    if (!restaurantStore.initialized) {
      console.log('Restaurant-Store wird initialisiert...');
      await restaurantStore.initialize();
      console.log('Restaurant-Store initialisiert:', {
        settings: restaurantStore.settings,
        logoDataUrl: restaurantStore.logoDataUrl,
        initialized: restaurantStore.initialized
      });
    }

  // Audio-Quelle setzen
  if (globalNotificationSound.value) {
    initializeSound(globalNotificationSound.value);
  }

  // QR-Code-Parameter aus URL extrahieren
    extractTableAndSessionParams();
  
  // Event-Listener für Navigation
  router.afterEach(() => {
      extractTableAndSessionParams();
    });
  
  // Listener für Fenstergrößenänderungen hinzufügen
    window.addEventListener('resize', handleResize);
  
  // Überprüfe Token und leite ggf. weiter
    const token = localStorage.getItem('auth_token');
    // Verwende den Pfad anstelle des Hash
    const currentPath = window.location.pathname;
  
  if (token && (currentPath === '/' || currentPath === '')) {
    console.log('Benutzer bereits eingeloggt, leite zum Dashboard weiter');
    
    // Überprüfen, ob es ein SuperAdmin ist und entsprechend weiterleiten
    const savedUser = localStorage.getItem('user');
    try {
      if (savedUser) {
        const user = JSON.parse(savedUser);
        const userRole = user.role?.toLowerCase() || '';
        if (userRole === 'superadmin') {
          console.log('SuperAdmin erkannt, leite zum SuperAdmin-Dashboard weiter');
          router.push('/superadmin/dashboard');
          return;
        }
      }
    } catch (e) {
      console.error('Fehler beim Parsen des Benutzers:', e);
    }
    
    // Standard: zum Admin-Dashboard
    router.push('/admin/dashboard');
  }
  
  // Berechtigungen für Benachrichtigungen anfordern
  requestNotificationPermission();
  } catch (error) {
    console.error('Fehler beim Initialisieren der App:', error);
  }
});

// Aufräumen der Event-Listener
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  // Verbindung trennen, wenn die App komplett zerstört wird
  console.log("App wird unmounted, trenne Küchen-WS...");
  orderStore.disconnectKitchenConnection(); 
})

// Funktion zum Extrahieren der Parameter
function extractTableAndSessionParams() {
  const currentPath = route.path
  const tableIdParam = route.params.tableId
  const sessionIdParam = route.params.sessionId
  
  // Prüfe, ob wir in der richtigen Route sind
  if (currentPath.includes('/table/') && tableIdParam) {
    // Tischwechsel: Wird ein ANDERER Tisch gescannt als zuletzt gespeichert,
    // verwerfen wir die alte Session komplett, damit fuer den neuen Tisch eine
    // frische Session erzeugt wird (verhindert Bestellungen am falschen Tisch
    // und alte "abgelaufene Session"-Effekte bei Stammgaesten).
    const previousTableId = localStorage.getItem('tableId')
    const sessionExpiredFlag = route.query.session_expired === '1'
    if (sessionExpiredFlag || (previousTableId && String(previousTableId) !== String(tableIdParam))) {
      localStorage.removeItem('sessionUniqueId')
      localStorage.removeItem('sessionNumericId')
      localStorage.removeItem('sessionId')
      localStorage.removeItem('shortPath')
      localStorage.removeItem('orderType')
      localStorage.removeItem('claimedDrops')
    }

    tableId.value = tableIdParam
    sessionId.value = sessionIdParam

    localStorage.setItem('tableId', tableIdParam)
    if (sessionIdParam) {
      localStorage.setItem('sessionId', sessionIdParam)
      if (!localStorage.getItem('sessionUniqueId')) {
        localStorage.setItem('sessionUniqueId', sessionIdParam)
      }
    }
    if (sessionIdParam) {
      localStorage.setItem('tableBaseUrl', `/table/${tableIdParam}/${sessionIdParam}`)
    } else {
      localStorage.setItem('tableBaseUrl', `/table/${tableIdParam}`)
    }

    console.log('[App] Parameter aus URL extrahiert:', tableIdParam, sessionIdParam)
  } 
  else {
    tableId.value = localStorage.getItem('tableId')
    sessionId.value = localStorage.getItem('sessionId')
    
    if (tableId.value && sessionId.value) {
      console.log('[App] Parameter aus localStorage geladen:', tableId.value, sessionId.value)
    }
  }
}

// Toggle Sidebar
const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
  localStorage.setItem('sidebarOpen', sidebarOpen.value)
}

// Responsive Sidebar – unterhalb von lg (1024px) ist die Sidebar ein Drawer
const handleResize = () => {
  if (window.innerWidth < 1024) {
    sidebarOpen.value = false
    localStorage.setItem('sidebarOpen', 'false')
  }
}

// Sidebar automatisch bei Seitenwechsel auf Mobilgeräten/Tablets schließen
watch(route, () => {
  if (window.innerWidth < 1024) {
    sidebarOpen.value = false
    localStorage.setItem('sidebarOpen', 'false')
  }
})

// Routen mit `meta.layout === 'fullscreen'` (z. B. Kellner-App auf Mobile)
// nutzen ihr eigenes Layout — Admin-Header und SideNav werden ausgeblendet.
const isFullscreenLayout = computed(() => route.meta?.layout === 'fullscreen')

// Bestimmt, ob Header angezeigt werden soll (Admin/SuperAdmin/Staff)
const shouldShowHeader = computed(() => {
  if (isFullscreenLayout.value) return false
  return isAdminPanel.value || isSuperAdminPanel.value || isStaffPanel.value
})

// Bestimmt, ob wir im Admin-Bereich sind
const isAdminPanel = computed(() => {
  return route.path.startsWith('/admin/')
})

// Bestimmt, ob wir im SuperAdmin-Bereich sind
const isSuperAdminPanel = computed(() => {
  return route.path.startsWith('/superadmin/')
})

// Bestimmt, ob wir im Staff-Bereich sind (Küche/Kellner)
const isStaffPanel = computed(() => {
  return route.path === '/kitchen' || route.path === '/waiter-dashboard'
})

// Bestimmt, ob wir im Kundenbereich sind
const isCustomerView = computed(() => {
  const p = route.path
  return !isAdminPanel.value && !isSuperAdminPanel.value && !isStaffPanel.value
    && p !== '/login' && !p.startsWith('/superadmin')
})

const customerGamesOn = computed(() => areCustomerGamesEnabled())

// Customer session info for gamification
const customerSessionId = computed(() => {
  return parseInt(localStorage.getItem('sessionNumericId')) || null
})
const customerSessionIdentifier = computed(() => {
  return localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''
})
const guestProfileId = computed(() => {
  return parseInt(localStorage.getItem('guestProfileId')) || null
})

function navigateToCategory(category) {
  router.push({ path: '/menu', query: { category } })
}

function handleGameAddToCart(product) {
  console.log('[App] Game item added to cart:', product?.name)
}

function handleGameDiscount(value) {
  console.log('[App] Game discount applied:', value)
}

// Load gamification features for customer views
watch(isCustomerView, (isCustomer) => {
  if (isCustomer && !gamificationStore.featuresLoaded) {
    gamificationStore.loadFeatures()
  }
}, { immediate: true })

// Composable für Benachrichtigungen über Provide/Inject bereitstellen
provide('showNotification', showDesktopNotification);
provide('playSound', playNotificationSound);
provide('playOrderSound', playOrderNotificationSound);
</script>

<style>
/* Allgemeine Gestaltung */
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

main {
  flex: 1;
}

/* Übergangsanimationen */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Reibungslose Übergänge */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
